import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Divider,
  Icon,
  Input,
  Modal,
  PasswordInput,
  Select,
  Space,
  Table,
  Tag,
  Text,
  useToast,
} from "@app/ui";
import { api } from "../api/client.js";
import { addOrgMember, changeOrgMemberRole, listUserOrgs, removeOrgMember } from "../api/dpp.js";
import { setUserPassword, unlockUser } from "../api/platform.js";
import { useAuth } from "../auth/AuthContext.js";
import { PasswordChecklist, passwordMeetsPolicy } from "../auth/PasswordChecklist.js";
import { RequirePermission } from "../components/RequirePermission.js";
import { ErrorAlert } from "../errors/index.js";
import { useFormatDateTime, useT } from "../i18n.js";
import type {
  AdminUserRow,
  OrgMemberRole,
  OrgWithCounts,
  OrgWithMemberRole,
  PlatformRole,
} from "../types.js";
import { generatePassword } from "./CreateUserModal.js";

const ORG_ROLES: readonly OrgMemberRole[] = ["admin", "editor", "viewer"];

/**
 * Platform admin: one user's platform roles and org memberships, plus session
 * revocation, enable/disable, lockout release and an admin-set password (the
 * support path when a user is locked out and mail doesn't reach them). Membership edits go through the regular
 * org-member endpoints (the API lets a platform admin act on any org).
 */
export function UserDetailModal({
  user,
  onClose,
  onChanged,
  roles,
  orgs,
}: {
  user: AdminUserRow | null;
  onClose: () => void;
  onChanged: () => void;
  roles: PlatformRole[];
  orgs: OrgWithCounts[];
}) {
  const t = useT();
  const toast = useToast();
  const fmt = useFormatDateTime();
  const { user: me, hasPermission } = useAuth();
  const [memberships, setMemberships] = useState<OrgWithMemberRole[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [newRole, setNewRole] = useState("");
  const [newOrgId, setNewOrgId] = useState("");
  const [newOrgRole, setNewOrgRole] = useState<OrgMemberRole>("editor");
  const [pwOpen, setPwOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [pwDone, setPwDone] = useState<string | null>(null);

  const canOrgs = hasPermission("org:manage");

  const loadMemberships = async (id: string) => {
    try {
      setMemberships(await listUserOrgs(id));
    } catch (e) {
      setError(e);
    }
  };

  useEffect(() => {
    setError(null);
    setMemberships([]);
    setNewRole("");
    setNewOrgId("");
    setPwOpen(false);
    setNewPassword("");
    setPwDone(null);
    if (user && canOrgs) void loadMemberships(user.id);
  }, [user?.id]);

  if (!user) return null;

  // Wraps a mutation: surfaces server-side guards (last superadmin, last org admin…).
  const run = async (fn: () => Promise<unknown>, okMsg: string): Promise<boolean> => {
    setError(null);
    setBusy(true);
    try {
      await fn();
      toast.success(okMsg);
      onChanged();
      if (canOrgs) await loadMemberships(user.id);
      return true;
    } catch (e) {
      setError(e);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const savePassword = async () => {
    const pw = newPassword;
    const ok = await run(() => setUserPassword(user.id, pw), t("adminUsers.passwordSet"));
    if (ok) {
      setPwDone(pw);
      setPwOpen(false);
      setNewPassword("");
    }
  };

  const assignableRoles = roles.filter((r) => !user.roles.includes(r.name));
  const joinableOrgs = orgs.filter((o) => !memberships.some((m) => m.id === o.id));
  const isSelf = me?.id === user.id;

  return (
    <Modal
      open
      onClose={onClose}
      title={user.displayName ? `${user.displayName} · ${user.email}` : user.email}
      width={720}
      footer={<Button onClick={onClose}>{t("common.close")}</Button>}
    >
      <Space direction="vertical" size="md" style={{ width: "100%" }}>
        {error != null && <ErrorAlert error={error} />}

        <div className="flex flex-wrap items-center gap-2">
          {user.disabled ? (
            <Tag color="error">{t("users.disabled")}</Tag>
          ) : (
            <Tag color="success">{t("users.active")}</Tag>
          )}
          <Tag color={user.mfaEnabled ? "success" : "default"}>
            {t("common.mfa")}: {user.mfaEnabled ? t("common.yes") : t("common.no")}
          </Tag>
          <Tag color={user.emailVerified ? "success" : "warning"}>
            {user.emailVerified ? t("adminUsers.emailVerified") : t("adminUsers.emailUnverified")}
          </Tag>
          {user.lockedUntil && <Tag color="warning">{t("adminUsers.locked")}</Tag>}
          <Text small secondary>
            {t("common.createdAt")}: {fmt(user.createdAt)} · {t("adminUsers.lastActive")}:{" "}
            {fmt(user.lastActiveAt)}
          </Text>
        </div>

        <Space size="sm">
          <RequirePermission permission="session:revoke">
            <Button
              size="sm"
              variant="subtle"
              icon={<Icon name="log-out" size={14} />}
              disabled={busy}
              onClick={() =>
                void run(
                  () => api(`/admin/users/${user.id}/revoke-sessions`, { method: "POST" }),
                  t("adminUsers.sessionsRevoked"),
                )
              }
            >
              {t("adminUsers.revokeSessions")}
            </Button>
          </RequirePermission>
          {!isSelf && (
            <RequirePermission permission="user:update">
              <Button
                size="sm"
                variant={user.disabled ? "subtle" : "danger"}
                disabled={busy}
                onClick={() =>
                  void run(
                    () =>
                      api(`/admin/users/${user.id}/disable`, {
                        method: "PATCH",
                        body: { disabled: !user.disabled },
                      }),
                    user.disabled ? t("adminUsers.enabled") : t("adminUsers.disabledMsg"),
                  )
                }
              >
                {user.disabled ? t("users.enable") : t("users.disable")}
              </Button>
            </RequirePermission>
          )}
          {!isSelf && user.lockedUntil && (
            <RequirePermission permission="user:update">
              <Button
                size="sm"
                variant="subtle"
                icon={<Icon name="lock" size={14} />}
                disabled={busy}
                onClick={() => void run(() => unlockUser(user.id), t("adminUsers.unlocked"))}
              >
                {t("adminUsers.unlock")}
              </Button>
            </RequirePermission>
          )}
          {!isSelf && (
            <RequirePermission permission="user:update">
              <Button
                size="sm"
                variant="subtle"
                icon={<Icon name="key" size={14} />}
                disabled={busy}
                onClick={() => {
                  setPwDone(null);
                  setPwOpen((v) => !v);
                }}
              >
                {t("adminUsers.setPassword")}
              </Button>
            </RequirePermission>
          )}
        </Space>

        {pwDone && (
          <Alert type="success">
            <Space direction="vertical" size="sm" style={{ width: "100%" }}>
              <Text>{t("adminUsers.passwordSetHandOver")}</Text>
              <Input value={pwDone} readOnly aria-label={t("adminUsers.password")} />
            </Space>
          </Alert>
        )}
        {pwOpen && (
          <div className="rounded-xl border border-border p-3">
            <Space direction="vertical" size="sm" style={{ width: "100%" }}>
              <Text small secondary>
                {t("adminUsers.setPasswordHelp")}
              </Text>
              <PasswordInput
                label={t("adminUsers.newPassword")}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <div className="flex flex-wrap items-center justify-between gap-2">
                <PasswordChecklist value={newPassword} />
                <Space size="sm">
                  <Button
                    size="sm"
                    variant="subtle"
                    icon={<Icon name="key" size={14} />}
                    onClick={() => setNewPassword(generatePassword())}
                  >
                    {t("adminUsers.generate")}
                  </Button>
                  <Button
                    size="sm"
                    variant="primary"
                    disabled={busy || !passwordMeetsPolicy(newPassword)}
                    onClick={() => void savePassword()}
                  >
                    {t("common.save")}
                  </Button>
                </Space>
              </div>
            </Space>
          </div>
        )}

        <Divider />
        <Text strong>{t("adminUsers.platformRoles")}</Text>
        <Text small secondary>
          {t("adminUsers.platformRoleHelp")}
        </Text>
        <div className="flex flex-wrap items-center gap-2">
          {user.roles.length === 0 && <Text secondary>—</Text>}
          {user.roles.map((r) => (
            <Tag key={r} color={r === "superadmin" ? "primary" : "default"}>
              <span className="inline-flex items-center gap-1">
                {r}
                {hasPermission("role:assign") && (
                  <Button
                    size="sm"
                    variant="subtle"
                    aria-label={t("common.remove")}
                    icon={<Icon name="close" size={12} />}
                    disabled={busy}
                    onClick={() =>
                      void run(
                        () =>
                          api(`/admin/users/${user.id}/roles/${encodeURIComponent(r)}`, { method: "DELETE" }),
                        t("adminUsers.roleRevoked", { role: r }),
                      )
                    }
                  />
                )}
              </span>
            </Tag>
          ))}
        </div>
        <RequirePermission permission="role:assign">
          <div className="flex flex-wrap items-end gap-2">
            <div style={{ width: 240, maxWidth: "100%" }}>
              <Select
                size="sm"
                placeholder={t("adminUsers.pickRole")}
                options={assignableRoles.map((r) => ({ label: r.name, value: r.name }))}
                value={newRole}
                onChange={setNewRole}
              />
            </div>
            <Button
              size="sm"
              icon={<Icon name="plus" size={14} />}
              disabled={!newRole || busy}
              onClick={() =>
                void run(
                  () => api(`/admin/users/${user.id}/roles`, { method: "POST", body: { role: newRole } }),
                  t("adminUsers.roleAssigned", { role: newRole }),
                ).then(() => setNewRole(""))
              }
            >
              {t("adminUsers.assignRole")}
            </Button>
          </div>
        </RequirePermission>

        {canOrgs && (
          <>
            <Divider />
            <Text strong>{t("adminUsers.orgs")}</Text>
            <Table
              rowKey={(m) => m.id}
              empty={t("adminUsers.noOrgs")}
              columns={[
                { key: "name", title: t("adminOrgs.name"), render: (m) => m.name },
                {
                  key: "role",
                  title: t("orgSettings.role"),
                  width: 170,
                  render: (m) => (
                    <Select
                      size="sm"
                      options={ORG_ROLES.map((r) => ({ label: t(`role.${r}`), value: r }))}
                      value={m.memberRole}
                      disabled={busy}
                      onChange={(v) =>
                        void run(
                          () => changeOrgMemberRole(m.id, user.id, v as OrgMemberRole),
                          t("adminUsers.orgRoleChanged", { org: m.name }),
                        )
                      }
                    />
                  ),
                },
                {
                  key: "actions",
                  title: "",
                  render: (m) => (
                    <Button
                      size="sm"
                      variant="danger"
                      aria-label={t("common.remove")}
                      icon={<Icon name="trash" size={14} />}
                      disabled={busy}
                      onClick={() =>
                        void run(
                          () => removeOrgMember(m.id, user.id),
                          t("adminUsers.orgRemoved", { org: m.name }),
                        )
                      }
                    />
                  ),
                },
              ]}
              data={memberships}
            />
            <div className="flex flex-wrap items-end gap-2">
              <div style={{ width: 280, maxWidth: "100%" }}>
                <Select
                  size="sm"
                  showSearch
                  placeholder={t("adminUsers.pickOrg")}
                  options={joinableOrgs.map((o) => ({ label: o.name, value: o.id, searchText: o.slug }))}
                  value={newOrgId}
                  onChange={setNewOrgId}
                />
              </div>
              <div style={{ width: 160 }}>
                <Select
                  size="sm"
                  options={ORG_ROLES.map((r) => ({ label: t(`role.${r}`), value: r }))}
                  value={newOrgRole}
                  onChange={(v) => setNewOrgRole(v as OrgMemberRole)}
                />
              </div>
              <Button
                size="sm"
                icon={<Icon name="plus" size={14} />}
                disabled={!newOrgId || busy}
                onClick={() => {
                  const target = orgs.find((o) => o.id === newOrgId);
                  void run(
                    () => addOrgMember(newOrgId, user.id, newOrgRole),
                    t("adminUsers.orgAdded", { org: target?.name ?? "" }),
                  ).then(() => setNewOrgId(""));
                }}
              >
                {t("adminUsers.addToOrg")}
              </Button>
            </div>
          </>
        )}
      </Space>
    </Modal>
  );
}
